import type { PlanComment } from '../components/PlanModal'

const LINE_ONLY_PREAMBLE =
  'Please revise the plan below based on these line-specific comments. Treat this as a targeted edit: change ONLY what each comment asks for, and reproduce every other line exactly as written — do not rephrase, reorder, restructure, or embellish any part of the plan the comments don\'t mention.'

const WITH_OVERALL_PREAMBLE =
  'Please revise the plan below based on the comments underneath it. The line-specific comments are targeted edits: change only what each one asks for. The overall comment may call for broader changes across the plan — apply it wherever it genuinely applies. Every line neither a line comment nor the overall comment implicates must still be reproduced exactly as written — do not rephrase, reorder, restructure, or embellish it.'

/**
 * ConversationView.handleRequestPlanChanges turns the comments a writer left on a plan into the
 * revision message sent back to the planning agent. lineIndex -1 is the sentinel for a
 * whole-plan comment (see PlanModal) rather than one anchored to a specific line, so it gets its
 * own OVERALL COMMENT block instead of a "Line: ... / Comment: ..." pair, and it changes the
 * instruction preamble: with only line comments the edit is purely targeted, but an overall
 * comment can require broader changes while still protecting every line neither comment touches.
 */
export function buildPlanRevisionRequest(planContent: string, comments: PlanComment[]): string {
  const lineComments = comments.filter((comment) => comment.lineIndex !== -1)
  const overallComment = comments.find((comment) => comment.lineIndex === -1)

  const feedbackParts: string[] = lineComments.map(
    (comment) => `Line: "${comment.lineText}"\nComment: "${comment.comment}"`
  )
  if (overallComment) {
    feedbackParts.push(`OVERALL COMMENT:\n"${overallComment.comment}"`)
  }

  const preamble = overallComment ? WITH_OVERALL_PREAMBLE : LINE_ONLY_PREAMBLE

  return `${preamble}\n\nCURRENT PLAN:\n${planContent}\n\nCOMMENTS:\n${feedbackParts.join('\n\n')}`
}

export interface ParsedPlanRevisionRequest {
  lineComments: Array<{ lineText: string; comment: string }>
  overallComment: string | null
}

/**
 * Inverse of buildPlanRevisionRequest, used by ConversationView to render the revision message
 * as a readable "Requested plan changes" card instead of the raw prompt text. Returns null when
 * the content is not something buildPlanRevisionRequest produced.
 */
export function parsePlanRevisionRequest(content: string): ParsedPlanRevisionRequest | null {
  const marker = '\n\nCOMMENTS:\n'
  const markerIndex = content.lastIndexOf(marker)
  if (markerIndex === -1 || !content.includes('\n\nCURRENT PLAN:\n')) return null
  const feedback = content.slice(markerIndex + marker.length)
  const lineComments: ParsedPlanRevisionRequest['lineComments'] = []
  let overallComment: string | null = null
  for (const part of feedback.split('\n\n')) {
    const lineMatch = part.match(/^Line: "([\s\S]*)"\nComment: "([\s\S]*)"$/)
    if (lineMatch) {
      lineComments.push({ lineText: lineMatch[1], comment: lineMatch[2] })
      continue
    }
    const overallMatch = part.match(/^OVERALL COMMENT:\n"([\s\S]*)"$/)
    if (overallMatch) overallComment = overallMatch[1]
  }
  if (lineComments.length === 0 && overallComment === null) return null
  return { lineComments, overallComment }
}

/** Markdown for the conversation bubble that shows what the writer asked to change. */
export function formatPlanRevisionMarkdown(parsed: ParsedPlanRevisionRequest): string {
  const sections = parsed.lineComments.map(
    ({ lineText, comment }) => `> ${lineText.replace(/\n/g, '\n> ')}\n\n${comment}`
  )
  if (parsed.overallComment !== null) {
    sections.push(`**Overall**\n\n${parsed.overallComment}`)
  }
  return `### Requested plan changes\n\n${sections.join('\n\n')}`
}
